// Router configuration separated from main.tsx to ensure proper initialization
import { lazy } from "react"
import {
  createBrowserRouter,
  Navigate,
  Outlet,
} from "react-router"

import type { RouteObject } from "react-router"

// Auth hooks
import { useSessionGuard } from "@/hooks/useSessionGuard"
// Components
import DebugPage from "@/pages/DebugPage"
import FlagsDebugPage from "@/pages/FlagsDebugPage"
import GlobeDebugPage from "@/pages/GlobeDebugPage"
import GlobePage from "@/pages/GlobePage"
import { PlaceCardPreview } from "@/pages/PlaceCardPreview"
import LandingPage from "@/pages/LandingPage"

// Lazy loaded components
const DisplayNamePage = lazy(() => import("@/pages/DisplayNamePage"))

// Loading fallback component
// eslint-disable-next-line react-refresh/only-export-components
function LoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
    </div>
  )
}

// Root layout component
// eslint-disable-next-line react-refresh/only-export-components
function RootLayout() {
  return <Outlet />
}

// Private route component
// eslint-disable-next-line react-refresh/only-export-components
function PrivateRoute({ children }: { children: React.ReactElement }) {
  const { initializing, isAuthed } = useSessionGuard()
  if (initializing) return <LoadingFallback />
  return isAuthed ? children : <Navigate to="/" replace />
}

// Error boundary component
// eslint-disable-next-line react-refresh/only-export-components
function ErrorBoundary() {
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="text-center space-y-4">
        <h1 className="text-2xl font-['Pioneer_Boulevard'] text-red-600">Application Error</h1>
        <p className="font-['Alga'] text-gray-600">Something went wrong. Please refresh the page.</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-white/90 text-gray-900 rounded-md hover:bg-white/80"
        >
          Refresh Page
        </button>
      </div>
    </div>
  )
}

// Route configuration
const routes: RouteObject[] = [
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        element: <LandingPage />,
      },
      {
        path: "display-name",
        element: <DisplayNamePage />,
      },
      {
        path: "trips",
        element: (
          <PrivateRoute>
            <GlobePage />
          </PrivateRoute>
        ),
      },
      {
        path: "trips/:tripId",
        element: (
          <PrivateRoute>
            <GlobePage />
          </PrivateRoute>
        ),
      },
      {
        path: "debug",
        element: (
          <PrivateRoute>
            <DebugPage />
          </PrivateRoute>
        ),
      },
      {
        path: "debug/globe",
        element: (
          <PrivateRoute>
            <GlobeDebugPage />
          </PrivateRoute>
        ),
      },
      {
        path: "debug/flags",
        element: (
          <PrivateRoute>
            <FlagsDebugPage />
          </PrivateRoute>
        ),
      },
      {
        path: "preview/place-card",
        element: <PlaceCardPreview />,
      },
    ],
  },
]

// Create and export the router
export const router = createBrowserRouter(routes)
